import { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { toast } from 'sonner';
import { Save, Loader2, RotateCcw, Building2 } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const MultisiteTemplateSettings = () => {
  const { token } = useAuth();
  const [html, setHtml] = useState('');
  const [isCustom, setIsCustom] = useState(false);
  const [saving, setSaving] = useState(false);
  const [resetting, setResetting] = useState(false);

  const load = () => {
    axios.get(API_URL + '/api/multisite/template?authorization=' + token)
      .then((r) => { setHtml(r.data.html_template); setIsCustom(r.data.is_custom); })
      .catch(() => toast.error('Failed to load multisite template'));
  };

  useEffect(() => { load(); }, [token]);

  const save = () => {
    setSaving(true);
    const fd = new FormData();
    fd.append('html_template', html);
    axios.post(API_URL + '/api/multisite/template?authorization=' + token, fd)
      .then((r) => { toast.success(r.data.message); setIsCustom(!!html.trim()); })
      .catch((e) => toast.error(e.response?.data?.detail || 'Failed to save'))
      .finally(() => setSaving(false));
  };

  const reset = () => {
    setResetting(true);
    axios.delete(API_URL + '/api/multisite/template?authorization=' + token)
      .then((r) => { toast.success(r.data.message); load(); })
      .catch(() => toast.error('Failed to reset'))
      .finally(() => setResetting(false));
  };

  return (
    <Card data-testid="multisite-template-settings">
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="flex items-center gap-2"><Building2 className="w-5 h-5 text-[#8CC63F]" />Multisite Contract Template</CardTitle>
            <CardDescription>
              Edit the HTML used for SME Multisite contract PDFs and eSignature documents. Placeholders: {'{{company_name}}, {{agreement_number}}, {{abn}}, {{special_instructions}}'} etc.
              Loops: {'{{#sites}}...{{/sites}}'} (site schedule rows) and {'{{#entities}}...{{/entities}}'} (Schedule 2). Saving overrides the default; Reset restores it.
            </CardDescription>
          </div>
          {isCustom ? <Badge className="bg-indigo-100 text-indigo-700">Custom template active</Badge> : <Badge variant="outline">Using default</Badge>}
        </div>
      </CardHeader>
      <CardContent className="space-y-3">
        <textarea
          value={html}
          onChange={(e) => setHtml(e.target.value)}
          className="w-full font-mono text-xs border rounded-lg p-3 bg-slate-950 text-green-400 focus:outline-none focus:ring-2 focus:ring-primary/50"
          style={{ height: 500, tabSize: 2, resize: 'vertical' }}
          spellCheck={false}
          data-testid="multisite-template-editor"
        />
        <div className="flex gap-3">
          <Button onClick={save} disabled={saving} className="bg-[#015492] hover:bg-[#013f6e]" data-testid="multisite-template-save">
            {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}Save as Custom Template
          </Button>
          <Button variant="outline" onClick={reset} disabled={resetting} data-testid="multisite-template-reset">
            <RotateCcw className="w-4 h-4 mr-2" />Reset to Default
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};
